import { Plus, Trash2, ArrowUp } from "lucide-react";
import { ImageField } from "@/admin/ImageField";

const AddBtn = ({ onClick, label, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="inline-flex items-center gap-1 text-xs font-heading font-bold border border-[#c9a227] px-3 py-1.5 hover:bg-[#c9a227]/20"><Plus className="w-3 h-3" /> {label}</button>
);
const Del = ({ onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="p-2 text-[#991b1b] hover:bg-[#991b1b]/10"><Trash2 className="w-4 h-4" /></button>
);

const setAt = (list, i, patch) => list.map((x, j) => (j === i ? { ...x, ...patch } : x));
const move = (list, i) => (i === 0 ? list : [...list.slice(0, i - 1), list[i], list[i - 1], ...list.slice(i + 1)]);

export const InfoboxEditor = ({ fields, onChange, defaults = [] }) => {
  const missing = defaults.filter((d) => !fields.some((f) => f.label === d));
  return (
    <div data-testid="infobox-editor" className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="flex gap-2 items-start">
          <input value={f.label} onChange={(e) => onChange(setAt(fields, i, { label: e.target.value }))} placeholder="Label" data-testid={`infobox-label-${i}`} className="admin-input w-2/5" />
          <textarea rows={1} value={f.value} onChange={(e) => onChange(setAt(fields, i, { value: e.target.value }))} placeholder="Value — use [[Page]] to link" data-testid={`infobox-value-${i}`} className="admin-input flex-1" />
          <button type="button" onClick={() => onChange(move(fields, i))} className="p-2 hover:bg-[#c9a227]/20"><ArrowUp className="w-4 h-4" /></button>
          <Del onClick={() => onChange(fields.filter((_, j) => j !== i))} testid={`infobox-remove-${i}`} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <AddBtn label="Add field" testid="infobox-add" onClick={() => onChange([...fields, { label: "", value: "" }])} />
        {missing.length > 0 && <AddBtn label={`Add template fields (${missing.length})`} testid="infobox-add-template" onClick={() => onChange([...fields, ...missing.map((label) => ({ label, value: "" }))])} />}
      </div>
    </div>
  );
};

export const GalleryEditor = ({ items, onChange }) => (
  <div data-testid="gallery-editor">
    <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {items.map((g, i) => (
        <div key={i} className="border border-[#c9a227]/40 p-3 bg-white/40">
          <ImageField value={g.url} onChange={(url) => onChange(setAt(items, i, { url }))} aspectKey={g.aspect} onAspect={(aspect) => onChange(setAt(items, i, { aspect }))} aspectFallback="square" testid={`gallery-image-${i}`} />
          <input value={g.caption} onChange={(e) => onChange(setAt(items, i, { caption: e.target.value }))} placeholder="Caption" className="admin-input mt-2" />
          <Del onClick={() => onChange(items.filter((_, j) => j !== i))} testid={`gallery-remove-${i}`} />
        </div>
      ))}
    </div>
    {items.length < 12 && <div className="mt-3"><AddBtn label="Add gallery slot" testid="gallery-add" onClick={() => onChange([...items, { url: "", caption: "", aspect: "square" }])} /></div>}
  </div>
);

export const TreeEditor = ({ nodes, onChange, titles }) => (
  <div data-testid="tree-editor" className="space-y-2">
    {nodes.map((n, i) => (
      <div key={n.id} className="grid md:grid-cols-[1.3fr_1.2fr_1fr_0.8fr_1.2fr_auto] gap-2 items-center border-b border-[#c9a227]/20 pb-2">
        <input value={n.name} onChange={(e) => onChange(setAt(nodes, i, { name: e.target.value }))} placeholder="Name" data-testid={`tree-name-${i}`} className="admin-input" />
        <select value={n.parent_id} onChange={(e) => onChange(setAt(nodes, i, { parent_id: e.target.value }))} data-testid={`tree-parent-${i}`} className="admin-input">
          <option value="">— Root (no parent) —</option>
          {nodes.filter((p) => p.id !== n.id).map((p) => <option key={p.id} value={p.id}>Child of {p.name || "(unnamed)"}</option>)}
        </select>
        <input value={n.spouse} onChange={(e) => onChange(setAt(nodes, i, { spouse: e.target.value }))} placeholder="Spouse" className="admin-input" />
        <input value={n.years} onChange={(e) => onChange(setAt(nodes, i, { years: e.target.value }))} placeholder="Years" className="admin-input" />
        <select value={n.link_slug} onChange={(e) => onChange(setAt(nodes, i, { link_slug: e.target.value }))} className="admin-input">
          <option value="">No page link</option>
          {titles.map((t) => <option key={t.slug} value={t.slug}>{t.title}</option>)}
        </select>
        <Del onClick={() => onChange(nodes.filter((_, j) => j !== i).map((x) => (x.parent_id === n.id ? { ...x, parent_id: "" } : x)))} testid={`tree-remove-${i}`} />
      </div>
    ))}
    <AddBtn label="Add family member" testid="tree-add" onClick={() => onChange([...nodes, { id: Math.random().toString(36).slice(2, 10), name: "", parent_id: "", spouse: "", years: "", link_slug: "" }])} />
  </div>
);
